/**
 * Vectorisation gratuite depuis l'éditeur (Daniel 09.10.2026) via /api/vectorize-dtf (serveur : JWT, plafond, SVG nettoyé).
 * La recommandation suit le banc du 09.10 : logo à plat (≤ 10 % hors des 8 couleurs principales) ET côté le plus court ≥ 150 px →
 * vectoriser direct ; dégradé/photo → jamais par défaut ; trop petit → déconseillé. Le client peut insister (avertissement).
 */
import { siteAccessToken } from './exportPlanche';
import type { LogoMetrics } from './logoMetrics';

export class VectorizeAuthError extends Error {}
export class VectorizeQuotaError extends Error {}

export type VectoVerdict = { ok: true; reason: 'plat' } | { ok: false; reason: 'degrade' | 'petit' | 'inconnu' };

export function recommendVectorize(m: LogoMetrics | null | undefined): VectoVerdict {
	if (!m) return { ok: false, reason: 'inconnu' };
	const flat = m.flatPct !== null && m.flatPct <= 10;
	const big = Math.min(m.w, m.h) >= 150;
	if (!flat && m.flatPct !== null) return { ok: false, reason: 'degrade' };
	if (!big) return { ok: false, reason: 'petit' };
	if (m.flatPct === null) return { ok: false, reason: 'inconnu' };
	return { ok: true, reason: 'plat' };
}

const MAX_CHARS = 4_000_000;

/** Prépare l'image à envoyer : PNG (alpha gardé), réduit à 1800 px max si trop lourd. */
async function toPayload(src: string): Promise<string> {
	if (src.startsWith('data:image/png;base64,') && src.length <= MAX_CHARS) return src;
	const im = await new Promise<HTMLImageElement>((resolve, reject) => { const i = new Image(); i.onload = () => resolve(i); i.onerror = () => reject(new Error('image')); i.src = src; });
	let w = im.naturalWidth || 1;
	let h = im.naturalHeight || 1;
	for (const max of [1800, 1400, 1100, 900]) {
		const s = Math.min(1, max / Math.max(w, h));
		const c = document.createElement('canvas');
		c.width = Math.max(1, Math.round(w * s));
		c.height = Math.max(1, Math.round(h * s));
		const ctx = c.getContext('2d');
		if (!ctx) throw new Error('canvas');
		ctx.drawImage(im, 0, 0, c.width, c.height);
		const u = c.toDataURL('image/png');
		if (u.length <= MAX_CHARS) return u;
	}
	throw new Error('image trop lourde');
}

/** Retourne le SVG nettoyé (texte). */
export async function vectorizeImage(src: string): Promise<{ svg: string; remaining: number }> {
	const token = siteAccessToken();
	if (!token) throw new VectorizeAuthError('non connecté');
	const image = await toPayload(src);
	const r = await fetch('/api/vectorize-dtf', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ image }) });
	const j = (await r.json().catch(() => ({}))) as { svg?: string; remaining?: number; error?: string; code?: string };
	if (r.status === 401 || j.code === 'auth') throw new VectorizeAuthError(j.error || 'non connecté');
	if (r.status === 429 || j.code === 'quota') throw new VectorizeQuotaError(j.error || 'plafond');
	if (!r.ok || !j.svg) throw new Error(j.error || `vectorize ${r.status}`);
	return { svg: j.svg, remaining: Number(j.remaining ?? 0) };
}
